import React, { FC, useState } from "react";
import clsx from "clsx";
import { Button } from "../Button";
import { trackEvent } from "utility/mixpanel";
import { useTranslation } from "lib/i18n/use-translation";
import styles from "./filter-bar.module.scss";
import { ReactComponent as ChevronIcon } from "Assets/icon/icon-chevron.svg";

export interface FilterOption {
  id: string;
  label: string;
  value: string;
}

export type FilterBarVariant = "horizontal" | "vertical";

export interface FilterBarProps extends React.HTMLAttributes<HTMLDivElement> {
  options: FilterOption[];
  activeFilters: string[];
  onFilterChange: (filter: string) => void;
  variant?: FilterBarVariant;
  filterGroupName?: string;
  className?: string;
  filterGroupCount?: number;
  filterGroupOpen?: boolean;
  libraryType?: string;
  categoryListPosition?: number;
}

const FilterBar: FC<FilterBarProps> = ({
  options,
  activeFilters,
  onFilterChange,
  variant = "horizontal",
  filterGroupName,
  className,
  filterGroupCount,
  filterGroupOpen = false,
  libraryType,
  categoryListPosition,
  ...rest
}) => {
  const { t } = useTranslation("components");
  const [isDropdownOpen, setIsDropdownOpen] = useState(filterGroupOpen);

  const handleFilterClick = (value: string, index: number) => {
    const isSelected = activeFilters.includes(value);
    trackEvent(`Filter Option ${isSelected ? "Deselected" : "Selected"}`, {
      "Option Name": value,
      "Category Name": filterGroupName,
      "Content Type": libraryType,
      "Option List Position": index + 1,
    });
    onFilterChange(value);
  };

  const handleGroupDropdownClick = () => {
    if (filterGroupName && libraryType) {
      trackEvent(`Filter Category ${!isDropdownOpen ? "Opened" : "Closed"}`, {
        "Category Name": filterGroupName,
        "Content Type": libraryType,
        "Category List Position": categoryListPosition,
      });
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  return (
    <div
      className={clsx(styles.filterBar, styles[variant], className)}
      role="toolbar"
      aria-label={t("components:filterBar.toolbarA11y")}
      {...rest}
    >
      {filterGroupName && (
        <Button
          variant="ghost"
          className={styles.filterGroupName}
          onClick={handleGroupDropdownClick}
          icon={
            <ChevronIcon
              className={isDropdownOpen ? styles.chevronUp : styles.chevronDown}
            />
          }
          iconPosition="end"
        >
          {filterGroupCount !== undefined
            ? `${filterGroupName} (${filterGroupCount})`
            : filterGroupName}
        </Button>
      )}

      {((variant === "vertical" && isDropdownOpen) ||
        variant === "horizontal") && (
        <div
          className={clsx(styles.filterOptions, styles[variant])}
          role="group"
          aria-label={t("components:filterBar.optionsA11y")}
        >
          {options.map((option, index) => (
            <Button
              key={option.value}
              variant="ghost"
              className={clsx(
                styles.filterOption,
                styles[variant],
                activeFilters.includes(option.id) && styles.active
              )}
              onClick={() => handleFilterClick(option.id, index)}
            >
              <span className={styles.filterOptionLabel}>{option.label}</span>
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterBar;
